import { useEffect } from "preact/hooks";
import { corePictureToTransportPicture } from "@brickme/project-core/src/model/transport.ts";
import { numberOfBrickColours } from "@brickme/project-core/src/model/picture.ts";
import { usePicture } from "@brickme/frontend-common/src/picture-edit/picture-edit-context.tsx";
import { useCurrentBrickedBuild } from "@brickme/frontend-common/src/picture-build/picture-build-context.tsx";
import FormErrors from "~/components/forms/form-errors.tsx";
import type { ModalProps } from "~/components/modal.tsx";
import Modal from "~/components/modal.tsx";
import ClipboardIcon from "~/components/icons/clipboard.tsx";
import type { FormValues } from "~/hooks/use-form.ts";
import useForm from "~/hooks/use-form.ts";
import TextField from "~/components/forms/text-field.tsx";
import FormRow from "~/components/forms/form-row.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import { useMainRegionApiClient } from "~/hooks/region/context.tsx";
import SummaryText from "~/components/summary-text.tsx";
import Button from "~/components/button.tsx";
import { sentryLogExtra } from "~/utils/sentry.ts";
import { useBuildSourcesMediaKeys } from "../editor-provider.tsx";
import type {
	GeneratePixAPicInstructionsMutation,
	GeneratePixAPicInstructionsMutationVariables,
} from "./generate-pix-a-pic-instructions-mutation.api.generated.ts";
import generatePixAPicInstructionsMutation from "./generate-pix-a-pic-instructions-mutation.api.graphql";
import classes from "./get-instructions-modal.module.css";

const formId = "get-instructions-form";

type GetInstructionsModalProps = Pick<ModalProps, "id"> & {
	readonly code: string;
	readonly numberOfKits: number;
	readonly onSuccess?: () => void;
};

function GetInstructionsModal({
	id,
	code,
	numberOfKits,
	onSuccess,
}: GetInstructionsModalProps) {
	const t = useTranslator();
	const { picture } = usePicture();
	const bricked = useCurrentBrickedBuild();
	const { sourceImageKey, backgroundMaskImageKey } = useBuildSourcesMediaKeys();
	// Really want this to be selected to using same palette. But code usages are added, which is at main.
	const apiClient = useMainRegionApiClient();
	const {
		formProps,
		isWorking,
		formError,
		successValues,
		formRef,
		resetFormState,
	} = useForm({
		id: formId,
		async onSubmit(values: FormValues) {
			if (!bricked) {
				throw new Error("Project not built");
			}
			if (!sourceImageKey) {
				throw new Error("Waiting for image to upload");
			}

			const { brickCounts } = bricked;
			const email = values.email as string;
			const name = values.name as string;

			sentryLogExtra.setInstructionsDestination({ email, name });

			await apiClient.mutate<
				GeneratePixAPicInstructionsMutation,
				GeneratePixAPicInstructionsMutationVariables
			>(generatePixAPicInstructionsMutation, {
				name,
				email,
				code,
				numberOfKits,
				sourceImageKey,
				backgroundMaskImageKey,
				picture: corePictureToTransportPicture(
					picture,
					numberOfBrickColours(brickCounts),
				),
			});
			formRef.current?.reset();
			onSuccess?.();
		},
	});
	useEffect(() => {
		if (!open) {
			resetFormState();
		}
	}, [resetFormState, open]);

	return (
		<Modal id={id} size="small" working={isWorking}>
			<form {...formProps} className={classes["form"]}>
				<ClipboardIcon className={classes["icon"]} />
				<h2>{t("Get instructions")}</h2>
				{successValues ? (
					<>
						<div className={classes["success"]}>
							{t(
								"We're working on your instructions and will send them to {email} when they're ready. If it's not in your inbox in a few minutes then please check your spam or junk folder.",
								successValues as Record<string, string>,
							)}
						</div>
						<Button
							type="button"
							variant="blue"
							commandfor={id}
							command="close"
						>
							{t("Done")}
						</Button>
					</>
				) : (
					<>
						<SummaryText>
							{t(
								`We'll send the instructions to your email. Please add your name to improve deliverability.`,
							)}
						</SummaryText>
						<FormRow>
							<TextField
								type="email"
								name="email"
								label={t("Email")}
								placeholder={t("Enter email")}
								required
								autoComplete="email"
								disabled={isWorking}
							/>
						</FormRow>
						<FormRow>
							<TextField
								type="text"
								name="name"
								label={t("Name")}
								placeholder={t("What's your name")}
								required
								autoComplete="given-name"
								disabled={isWorking}
							/>
						</FormRow>
						{sourceImageKey === undefined && (
							<em>{t("Uploading image, please wait")}</em>
						)}
						<FormErrors errors={[formError]} />
						<Button
							type="submit"
							variant="blue"
							form={formId}
							disabled={sourceImageKey === undefined || isWorking}
						>
							{t("Send instructions")}
						</Button>
						<Button
							type="button"
							variant="text"
							commandfor={id}
							command="close"
							disabled={isWorking}
						>
							{t("Cancel")}
						</Button>
					</>
				)}
			</form>
		</Modal>
	);
}

export default GetInstructionsModal;
